import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { deleteUser } from 'api/UserController';
import errorReporter from '@app/errorReporter';
import {
  UserStatusTypes,
  userRolesToLabelMap,
  ROLES,
  UserRoles,
} from '@bb/users';
import { isEqual } from 'lodash';
import {
  capitalize,
  featureEnabled,
  isBrokerBayAdminEmail,
  SUPPORT_EMAIL,
} from 'utils';
import { LayoutContext, AuthContext } from 'contexts';
import {
  Avatar,
  Space,
  Dropdown,
  Button,
  Menu,
  MenuItem,
  Icon,
  TableProps,
  message,
} from 'components';
import { FilterDropdownProps } from 'antd/lib/table/interface';
import { useTableResizer } from 'hooks';
import { SupraSystemSubscriptionStatus } from '@bb/domain/dist/types/SupraSystemSubscription';
import { StyledTable, UserStatusTag } from '../../../components';
import ManageUsersContext from '../ManageUsersContext';
import ManageUsersPagination from './ManageUsersPagination';
import BranchFilterDropdown from './BranchFilterDropdown';
import { addDatacy, addDatacyToHeader } from '../util';

const UsersTable: React.FC = () => {
  const { transition } = React.useContext(LayoutContext);
  const { auth, isBrokerBay } = React.useContext(AuthContext);
  const history = useHistory();
  const {
    activeUsers,
    activeUsersCount,
    isUsersLoading: isLoading,
    locations,
    statusFilters,
    existingUserStatus,
    roleFilters,
    existingUserRoles,
    usersSortControls: sortControls,
    selectedUsers,
    setActiveUsers,
    setStatusFilters,
    setRoleFilters,
    setUsersSortControls: setSortControls,
    setSelectedUsers,
  } = React.useContext(ManageUsersContext);
  const { sort, isDesc } = sortControls;

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(
    () => () => {
      swal.close();
    },
    [history],
  );

  const tableWrapperRef = React.useRef<HTMLDivElement>();

  const { tableHeight, tableHeaderHeight } = useTableResizer({
    wrapperRef: tableWrapperRef,
  });

  const forbiddenBBAdmin = React.useCallback(
    (email: string) => {
      if (isBrokerBayAdminEmail(email) && !isBrokerBay) {
        message.error(
          `You cannot modify this BrokerBady Admin account. Please contact ${SUPPORT_EMAIL} for assistance.`,
        );
        return true;
      }
      return false;
    },
    [isBrokerBay],
  );

  const onUserClick = (userId: string, email: string) => {
    const isForbidden = forbiddenBBAdmin(email);
    if (isForbidden) return;

    history.push(`/users/edit/${userId}`);
  };

  const canDelete = useCallback(
    (targetUserRole: UserRoles) =>
      (ROLES[targetUserRole] !== ROLES.OWNER &&
        ROLES[auth.role] >= ROLES[targetUserRole]) ||
      isBrokerBayAdminEmail(auth.email),
    [auth.role],
  );

  const deleteAlert = (userId: string, email: string) => {
    const isForbidden = forbiddenBBAdmin(email);
    if (isForbidden) return;

    swal(
      {
        title: 'Delete User',
        text: 'Are you sure you want to delete this user?\nThis operation is permanent and cannot be undone.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#DD6B55',
        confirmButtonText: 'Delete',
        closeOnConfirm: true,
      },
      async () => {
        try {
          await deleteUser({ userId });
          // Only remove users from FE if req is successful
          setActiveUsers(activeUsers.filter(({ id }) => id !== userId));
          message.success('Deleted User');
        } catch (err) {
          message.error('Error deleting user');
          errorReporter.error(err);
        }
      },
    );
  };

  const columns: TableProps['columns'] = [
    {
      width: 200,
      title: 'Name',
      key: 'name',
      dataIndex: 'name',
      sorter: true,
      render: (name: string, user: any) => (
        <Space align="center" size="middle">
          <Avatar user={user.avatar} />
          {`${user.firstName} ${user.lastName}`}
        </Space>
      ),
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort === 'firstName'
        ? { defaultSortOrder: isDesc ? 'descend' : 'ascend' }
        : {}),
      onCell: addDatacy('users-name'),
      onHeaderCell: addDatacyToHeader('user-names'),
    },
    {
      width: 100,
      title: 'Role',
      key: 'role',
      dataIndex: 'role',
      align: 'center',
      filteredValue: roleFilters,
      filters: existingUserRoles.map(key => ({
        text: capitalize(key),
        value: key,
      })),
      filterIcon: () => (
        <Icon
          width={12}
          type="antdFilterFilled"
          color={roleFilters?.length ? 'primary' : undefined}
        />
      ),
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort === 'role'
        ? { defaultSortOrder: isDesc ? 'descend' : 'ascend' }
        : {}),
      onCell: addDatacy('users-role'),
      onHeaderCell: addDatacyToHeader('users-role'),
    },
    {
      width: 100,
      title: 'Status',
      key: 'status',
      dataIndex: '',
      align: 'center',
      filteredValue: statusFilters,
      filters: existingUserStatus.map(status => ({
        text: capitalize(status),
        value: status,
      })),
      filterIcon: () => (
        <Icon
          width={12}
          type="antdFilterFilled"
          color={statusFilters?.length ? 'primary' : undefined}
        />
      ),
      render: ({ disabled, isUnverified }) => (
        <UserStatusTag disabled={disabled} isUnverified={isUnverified} />
      ),
      onCell: addDatacy('users-status'),
      onHeaderCell: addDatacyToHeader('users-status'),
    },
    {
      width: 175,
      title: 'Email',
      key: 'email',
      dataIndex: 'email',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort === 'email'
        ? { defaultSortOrder: isDesc ? 'descend' : 'ascend' }
        : {}),
      onCell: addDatacy('users-email'),
      onHeaderCell: addDatacyToHeader('users-email'),
    },
    {
      width: 125,
      title: 'Branch',
      key: 'location',
      dataIndex: 'location',
      sorter: true,
      filterIcon: () => (
        <Icon
          width={12}
          type="antdFilterFilled"
          color={locations?.length ? 'primary' : undefined}
        />
      ),
      filterDropdown: (props: FilterDropdownProps) => (
        <BranchFilterDropdown {...props} />
      ),
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort === 'location'
        ? { defaultSortOrder: isDesc ? 'descend' : 'ascend' }
        : {}),
      onCell: addDatacy('users-branch'),
      onHeaderCell: addDatacyToHeader('users-branch'),
    },
    {
      width: 250,
      title: 'Address',
      key: 'address',
      dataIndex: 'address',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      ...(sort === 'address'
        ? { defaultSortOrder: isDesc ? 'descend' : 'ascend' }
        : {}),
      onCell: addDatacy('users-address'),
      onHeaderCell: addDatacyToHeader('users-address'),
    },
    {
      width: 80,
      title: '',
      key: 'actions',
      dataIndex: 'actions',
      align: 'center',
      render: (text: string, user: any) =>
        featureEnabled('disableDeleteKeyholderFeature') ? (
          !user.inRoster &&
          user.subscriptionStatus ===
            SupraSystemSubscriptionStatus.unsubscribed && (
            <Dropdown
              placement="topLeft"
              trigger={['click']}
              getPopupContainer={node => node.parentElement}
              dropdownRender={() => (
                <div
                  onClick={e => e.stopPropagation()}
                  data-cy="users-dropdown"
                >
                  <Menu>
                    <MenuItem
                      key={'delete'}
                      onClick={() => deleteAlert(user.id, user.email)}
                      disabled={!canDelete(user.rawRole)}
                      data-cy="users-dropdown-delete"
                    >
                      Delete User
                    </MenuItem>
                  </Menu>
                </div>
              )}
            >
              <Button
                type="text"
                size="small"
                onClick={e => e.stopPropagation()}
              >
                <Icon type="ellipsis" width={25} />
              </Button>
            </Dropdown>
          )
        ) : (
          <Dropdown
            placement="topLeft"
            trigger={['click']}
            getPopupContainer={node => node.parentElement}
            dropdownRender={() => (
              <div onClick={e => e.stopPropagation()} data-cy="users-dropdown">
                <Menu>
                  <MenuItem
                    key={'delete'}
                    onClick={() => deleteAlert(user.id, user.email)}
                    disabled={!canDelete(user.rawRole)}
                    data-cy="users-dropdown-delete"
                  >
                    Delete User
                  </MenuItem>
                </Menu>
              </div>
            )}
          >
            <Button type="text" size="small" onClick={e => e.stopPropagation()}>
              <Icon type="ellipsis" width={25} />
            </Button>
          </Dropdown>
        ),
      onCell: addDatacy('users-options'),
      onHeaderCell: addDatacyToHeader('users-options'),
    },
  ];
  const processedColumns = columns.filter(Boolean);

  const dataSource =
    auth &&
    activeUsers?.map(
      ({
        id,
        firstName,
        lastName,
        email,
        location,
        address,
        role,
        avatarFilestackId,
        disabled,
        isUnverified,
        supraSubscriptionStatus,
        inRoster,
      }) => ({
        id,
        key: id,
        firstName,
        lastName,
        avatar: { firstName, lastName, avatarFilestackId },
        email,
        role: userRolesToLabelMap[role],
        rawRole: role,
        disabled,
        isUnverified,
        location: auth.org.locations[Number(location)].name,
        address:
          auth.org.addresses?.[Number(location)]?.[Number(address)]?.address,
        subscriptionStatus: supraSubscriptionStatus,
        inRoster,
      }),
    );

  return (
    <>
      <div ref={tableWrapperRef}>
        <StyledTable
          style={{
            transition: `all ${transition}s`,
          }}
          pagination={false}
          $height={tableHeight}
          $headerHeight={tableHeaderHeight}
          showSorterTooltip={false}
          loading={isLoading}
          dataSource={dataSource}
          columns={processedColumns}
          scroll={{
            x: '100%',
            y: `calc(100% - ${tableHeaderHeight}px)`,
          }}
          onRow={user => ({
            onClick: () => onUserClick(user.id, user.email),
          })}
          onChange={(
            pagination,
            filters,
            sorter: { column: any; order: any },
          ) => {
            const newFilters = filters.status ?? [];
            if (!isEqual(statusFilters, newFilters)) {
              setStatusFilters(newFilters as UserStatusTypes[]);
            }

            const newRoleFilters = filters.role ?? [];
            if (!isEqual(roleFilters, newRoleFilters)) {
              setRoleFilters(newRoleFilters as (keyof typeof UserRoles)[]);
            }

            if (sorter.column) {
              const newControls = {
                sort:
                  sorter.column.dataIndex !== 'name'
                    ? sorter.column.dataIndex
                    : 'firstName',
                isDesc: sorter.order === 'descend',
              };
              if (!isEqual(sortControls, newControls))
                setSortControls(newControls);
            } else {
              setSortControls({ sort: '', isDesc: false });
            }
          }}
          rowSelection={{
            type: 'checkbox',
            onChange: (selectedRowKeys, selectedRows) => {
              setSelectedUsers(selectedRowKeys as string[]);
            },
            getCheckboxProps: record => ({
              id: record.id,
            }),
          }}
          data-cy="users-table"
        />
        <ManageUsersPagination count={activeUsersCount} tableName="users" />
      </div>
    </>
  );
};

export default UsersTable;
